export class HTTPError extends Error {
  constructor(message: string, readonly status: number, readonly retryAfterMs = 0) {
    super(message);
    this.name = status === 401 ? 'AuthRequiredError' : 'HTTPError';
  }
}

export async function fetchJSON<T>(
  path: string,
  options: RequestInit = {},
  timeout = 10_000,
  externalSignal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeout);
  const onAbort = () => controller.abort();
  const signals = [...new Set([options.signal, externalSignal].filter((signal): signal is AbortSignal => Boolean(signal)))];
  for (const signal of signals) {
    signal.addEventListener('abort', onAbort, { once: true });
    if (signal.aborted) onAbort();
  }
  try {
    if (controller.signal.aborted) throw new DOMException('Request cancelled', 'AbortError');
    const supplied = new Headers(options.headers);
    const headers = {
      ...(!supplied.has('Accept') ? { Accept: 'application/json' } : {}),
      ...(typeof options.body === 'string' && !supplied.has('Content-Type') ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers instanceof Headers || Array.isArray(options.headers) ? Object.fromEntries(supplied) : options.headers),
    };
    const response = await fetch(path, {
      ...options,
      credentials: 'include',
      headers,
      signal: controller.signal,
    });
    const payload = await response.json().catch((error: unknown) => {
      // A timeout while reading a successful response body is still a failure.
      if ((error as Error).name === 'AbortError') throw error;
      if (response.ok) throw new Error('network.unavailable');
      return {};
    }) as { error?: string };
    if (!response.ok) {
      const retryAfter = response.headers.get('Retry-After');
      const delay = retryAfter === null ? 0 : /^\d+$/.test(retryAfter)
        ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now();
      throw new HTTPError(payload.error ?? `HTTP ${response.status}`, response.status, Number.isFinite(delay) ? Math.max(0, delay) : 0);
    }
    return payload as T;
  } catch (error) {
    if ((error as Error).name === 'AbortError') {
      if (signals.some(signal => signal.aborted)) throw new DOMException('Request cancelled', 'AbortError');
      throw new Error('network.timeout');
    }
    if (error instanceof TypeError) throw new Error('network.unavailable');
    throw error;
  } finally {
    clearTimeout(timer);
    for (const signal of signals) signal.removeEventListener('abort', onAbort);
  }
}
